import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

interface Student {
  id: string;
  name: string;
  rollNo: string;
  class: string;
}

interface ReportData {
  studentId: string;
  reportType: string;
  priority: string;
  title: string;
  description: string;
  date: string;
  witnesses: string;
}

const ReportStudent = () => {
  const routes = all_routes;
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [reportData, setReportData] = useState<ReportData>({
    studentId: "",
    reportType: "",
    priority: "medium",
    title: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
    witnesses: "",
  });

  // Mock students data from teacher's classes
  const studentsData: Student[] = [
    { id: "1", name: "Emma Johnson", rollNo: "001", class: "10A Mathematics" },
    { id: "2", name: "Michael Smith", rollNo: "002", class: "10A Mathematics" },
    {
      id: "3",
      name: "Sofia Rodriguez",
      rollNo: "003",
      class: "10A Mathematics",
    },
    { id: "4", name: "James Wilson", rollNo: "004", class: "10A Mathematics" },
    { id: "5", name: "Aisha Patel", rollNo: "001", class: "11B Physics" },
    { id: "6", name: "David Chen", rollNo: "002", class: "11B Physics" },
    { id: "7", name: "Maria Garcia", rollNo: "001", class: "9C Mathematics" },
    { id: "8", name: "Alex Thompson", rollNo: "002", class: "9C Mathematics" },
  ];

  const reportTypes = [
    {
      value: "disciplinary",
      label: "Disciplinary Issue",
      icon: "ti-alert-triangle",
      color: "danger",
    },
    {
      value: "cheating",
      label: "Academic Dishonesty",
      icon: "ti-eye-off",
      color: "warning",
    },
    {
      value: "bullying",
      label: "Bullying/Harassment",
      icon: "ti-user-x",
      color: "danger",
    },
    {
      value: "disruption",
      label: "Class Disruption",
      icon: "ti-volume-x",
      color: "warning",
    },
    {
      value: "attendance",
      label: "Attendance Issue",
      icon: "ti-calendar-x",
      color: "info",
    },
    {
      value: "safety",
      label: "Safety Concern",
      icon: "ti-shield-x",
      color: "danger",
    },
    { value: "other", label: "Other", icon: "ti-dots", color: "secondary" },
  ];

  const priorities = [
    { value: "low", label: "Low Priority", color: "info" },
    { value: "medium", label: "Medium Priority", color: "warning" },
    { value: "high", label: "High Priority", color: "danger" },
    { value: "urgent", label: "Urgent", color: "danger" },
  ];

  const handleInputChange = (field: keyof ReportData, value: string) => {
    setReportData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitReport = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (
      !reportData.studentId ||
      !reportData.reportType ||
      !reportData.title ||
      !reportData.description
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    // Simulate API call
    console.log("Submitting report:", reportData);

    // Show success modal
    setShowSuccessModal(true);

    // Reset form after successful submission
    setTimeout(() => {
      setReportData({
        studentId: "",
        reportType: "",
        priority: "medium",
        title: "",
        description: "",
        date: new Date().toISOString().split("T")[0],
        witnesses: "",
      });
    }, 2000);
  };

  const selectedStudent = studentsData.find(
    (s) => s.id === reportData.studentId
  );
  const selectedReportType = reportTypes.find(
    (t) => t.value === reportData.reportType
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Report a Student</h3>
              <p className="text-muted mb-0">
                Report disciplinary issues or academic concerns
              </p>
            </div>
          </div>

          {/* Report Form */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="ti ti-alert-triangle me-2 text-warning"></i>
                Student Incident Report
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmitReport}>
                <div className="row g-4">
                  {/* Student Selection */}
                  <div className="col-md-6">
                    <label className="form-label">
                      Select Student <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={reportData.studentId}
                      onChange={(e) =>
                        handleInputChange("studentId", e.target.value)
                      }
                      required
                    >
                      <option value="">Choose a student...</option>
                      {studentsData.map((student) => (
                        <option key={student.id} value={student.id}>
                          {student.name} - {student.rollNo} ({student.class})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Report Type */}
                  <div className="col-md-6">
                    <label className="form-label">
                      Report Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={reportData.reportType}
                      onChange={(e) =>
                        handleInputChange("reportType", e.target.value)
                      }
                      required
                    >
                      <option value="">Select type...</option>
                      {reportTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Priority */}
                  <div className="col-md-6">
                    <label className="form-label">Priority Level</label>
                    <select
                      className="form-select"
                      value={reportData.priority}
                      onChange={(e) =>
                        handleInputChange("priority", e.target.value)
                      }
                    >
                      {priorities.map((priority) => (
                        <option key={priority.value} value={priority.value}>
                          {priority.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date */}
                  <div className="col-md-6">
                    <label className="form-label">Incident Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={reportData.date}
                      onChange={(e) =>
                        handleInputChange("date", e.target.value)
                      }
                      max={new Date().toISOString().split("T")[0]}
                    />
                  </div>

                  {/* Title */}
                  <div className="col-12">
                    <label className="form-label">
                      Report Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Brief summary of the incident..."
                      value={reportData.title}
                      onChange={(e) =>
                        handleInputChange("title", e.target.value)
                      }
                      required
                    />
                  </div>

                  {/* Description */}
                  <div className="col-12">
                    <label className="form-label">
                      Detailed Description{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={6}
                      placeholder="Provide a detailed description of the incident, including what happened, when it occurred, and any relevant context..."
                      value={reportData.description}
                      onChange={(e) =>
                        handleInputChange("description", e.target.value)
                      }
                      required
                    ></textarea>
                  </div>

                  {/* Witnesses */}
                  <div className="col-12">
                    <label className="form-label">Witnesses (Optional)</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="List any witnesses to the incident (students, staff, etc.)"
                      value={reportData.witnesses}
                      onChange={(e) =>
                        handleInputChange("witnesses", e.target.value)
                      }
                    ></textarea>
                  </div>

                  {/* Report Preview */}
                  {(selectedStudent || selectedReportType) && (
                    <div className="col-12">
                      <div className="alert alert-info">
                        <h6 className="alert-heading">Report Summary</h6>
                        {selectedStudent && (
                          <p className="mb-1">
                            <strong>Student:</strong> {selectedStudent.name} (
                            {selectedStudent.class})
                          </p>
                        )}
                        {selectedReportType && (
                          <p className="mb-1">
                            <strong>Type:</strong> {selectedReportType.label}
                          </p>
                        )}
                        <p className="mb-0">
                          <strong>Priority:</strong>
                          <span
                            className={`badge bg-${
                              priorities.find(
                                (p) => p.value === reportData.priority
                              )?.color
                            } ms-2`}
                          >
                            {
                              priorities.find(
                                (p) => p.value === reportData.priority
                              )?.label
                            }
                          </span>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="col-12">
                    <div className="d-flex gap-2">
                      <button type="submit" className="btn btn-danger">
                        <i className="ti ti-send me-2"></i>
                        Submit Report
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() =>
                          setReportData({
                            studentId: "",
                            reportType: "",
                            priority: "medium",
                            title: "",
                            description: "",
                            date: new Date().toISOString().split("T")[0],
                            witnesses: "",
                          })
                        }
                      >
                        <i className="ti ti-refresh me-2"></i>
                        Reset Form
                      </button>
                      <Link
                        to={routes.teacherDashboard}
                        className="btn btn-outline-secondary"
                      >
                        <i className="ti ti-arrow-left me-2"></i>
                        Cancel
                      </Link>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Guidelines */}
          <div className="card mt-4">
            <div className="card-header">
              <h6 className="card-title mb-0">
                <i className="ti ti-info-circle me-2"></i>
                Reporting Guidelines
              </h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h6 className="text-warning">Important Notes:</h6>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <i className="ti ti-check-circle text-success me-2"></i>
                      Be objective and factual in your description
                    </li>
                    <li className="mb-2">
                      <i className="ti ti-check-circle text-success me-2"></i>
                      Include specific dates, times, and locations
                    </li>
                    <li className="mb-2">
                      <i className="ti ti-check-circle text-success me-2"></i>
                      Document any immediate actions taken
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6 className="text-info">What Happens Next:</h6>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <i className="ti ti-arrow-right text-primary me-2"></i>
                      Report is sent to administration
                    </li>
                    <li className="mb-2">
                      <i className="ti ti-arrow-right text-primary me-2"></i>
                      Parents may be notified
                    </li>
                    <li className="mb-2">
                      <i className="ti ti-arrow-right text-primary me-2"></i>
                      Follow-up actions will be determined
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title text-success">
                  <i className="ti ti-check-circle me-2"></i>
                  Report Submitted Successfully
                </h5>
              </div>
              <div className="modal-body text-center">
                <div className="avatar avatar-xl bg-success-transparent mb-3">
                  <i className="ti ti-check fs-1 text-success"></i>
                </div>
                <h6>Your report has been submitted to administration.</h6>
                <p className="text-muted mb-0">
                  The administration team will review this report and take
                  appropriate action. You may be contacted for additional
                  information if needed.
                </p>
              </div>
              <div className="modal-footer border-0 justify-content-center">
                <button
                  className="btn btn-success"
                  onClick={() => setShowSuccessModal(false)}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportStudent;
